import React, { useEffect, useState } from "react";
import { Users, Plus, Power, ShieldCheck, Store, UserCircle, UserCog } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Label, EmptyState, InfoBadge } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const ROLE_META = {
  corporate_admin: { label: "Corporate Admin", color: "bg-indigo-50 text-indigo-700", icon: ShieldCheck },
  store_admin: { label: "Location Admin", color: "bg-amber-50 text-amber-700", icon: Store },
  employee: { label: "Employee", color: "bg-slate-100 text-slate-600", icon: UserCircle },
};

export default function TeamAccess() {
  const { user } = useAuth();
  const isCorp = user.role === "corporate_admin";
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [roleFor, setRoleFor] = useState(null);
  const [rForm, setRForm] = useState({ role: "employee", location_ids: [] });

  const load = () => api.get("/users").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isCorp) api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
  }, [isCorp]);

  const createCoAdmin = async () => {
    setSaving(true);
    try {
      await api.post("/corporate-admins", form);
      toast.success("Corporate admin added");
      setShowNew(false);
      setForm({ name: "", email: "", password: "" });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const toggle = async (u) => {
    try {
      await api.patch(`/users/${u.id}/status`, { active: !u.active });
      toast.success(u.active ? "Access revoked" : "Access restored");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const openRole = (u) => {
    setRoleFor(u);
    setRForm({ role: u.role, location_ids: u.location_ids || [] });
  };

  const saveRole = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${roleFor.id}/role`, rForm);
      toast.success("Role updated");
      setRoleFor(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const toggleLoc = (id) => setRForm((f) => ({
    ...f, location_ids: f.location_ids.includes(id) ? f.location_ids.filter((x) => x !== id) : [...f.location_ids, id],
  }));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="flex items-center gap-1.5"><Label>Access Control</Label>
            <InfoBadge title="Team & Access" testId="team-access-info"
              text={isCorp
                ? "Manage who can sign in to your company. Corporate admins manage co-admins, location admins and employee logins; revoking disables sign-in immediately while keeping the person's history."
                : "Manage the login accounts for employees at your location(s). Revoking disables sign-in immediately while keeping their history."} /></span>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Team & Access</h1>
        </div>
        {isCorp && (
          <Button onClick={() => setShowNew(true)} data-testid="new-coadmin-button" className="bg-[#4338CA] hover:bg-[#3730A3]">
            <Plus className="mr-1 h-4 w-4" /> Add corporate admin
          </Button>
        )}
      </div>

      {rows.length === 0 ? <EmptyState icon={Users} title="No user accounts yet" hint="Login accounts you create will appear here." /> : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="users-grid">
          {rows.map((u) => {
            const meta = ROLE_META[u.role] || { label: u.role, color: "bg-slate-100 text-slate-600", icon: Users };
            const Icon = meta.icon;
            return (
              <div key={u.id} className="rounded-xl border border-slate-200 bg-white p-5" data-testid={`user-${u.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-500"><Icon className="h-5 w-5" /></div>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${meta.color}`}>{meta.label}</span>
                </div>
                <p className="mt-3 truncate font-head text-lg font-semibold text-slate-900">{u.name || "—"}</p>
                <p className="truncate text-sm text-slate-400">{u.email}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className={`text-xs font-bold ${u.active ? "text-emerald-600" : "text-red-500"}`}>
                    {u.active ? "Active" : "Disabled"}
                  </span>
                  {u.is_self ? (
                    <span className="text-xs text-slate-400">You</span>
                  ) : (
                    <div className="flex items-center gap-2">
                      {isCorp && (
                        <Button size="sm" variant="outline" onClick={() => openRole(u)} data-testid={`role-user-${u.id}`}
                          className="text-slate-600 hover:bg-slate-50">
                          <UserCog className="mr-1 h-3.5 w-3.5" /> Role
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => toggle(u)} data-testid={`toggle-user-${u.id}`}
                        className={u.active ? "text-red-600 hover:bg-red-50" : "text-emerald-600 hover:bg-emerald-50"}>
                        <Power className="mr-1 h-3.5 w-3.5" /> {u.active ? "Revoke" : "Restore"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!isCorp && (
        <p className="text-sm text-slate-400">
          To add a login for an employee, open the employee from the <strong>Employees</strong> page.
        </p>
      )}

      <Dialog open={!!roleFor} onOpenChange={(v) => !v && setRoleFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change role · {roleFor?.name || roleFor?.email}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Role</Label>
              <Select value={rForm.role} onValueChange={(v) => setRForm({ ...rForm, role: v })}>
                <SelectTrigger data-testid="role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="corporate_admin">Corporate Admin</SelectItem>
                  <SelectItem value="store_admin">Location Admin</SelectItem>
                  <SelectItem value="employee">Employee</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {rForm.role === "store_admin" && (
              <div>
                <Label className="mb-2 block normal-case tracking-normal text-slate-600">Assigned locations</Label>
                <div className="max-h-48 space-y-1.5 overflow-y-auto">
                  {locations.map((l) => (
                    <label key={l.id} className="flex cursor-pointer items-center gap-2 text-sm" data-testid={`role-loc-${l.id}`}>
                      <input type="checkbox" checked={rForm.location_ids.includes(l.id)} onChange={() => toggleLoc(l.id)} />
                      {l.name}
                    </label>
                  ))}
                  {locations.length === 0 && <p className="text-sm text-slate-400">No locations yet.</p>}
                </div>
              </div>
            )}
            <p className="text-xs text-slate-400">
              {rForm.role === "corporate_admin" && "Full access across the whole company."}
              {rForm.role === "store_admin" && "Can manage only the selected location(s) and their employees."}
              {rForm.role === "employee" && "Standard access to their own profile only."}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleFor(null)}>Cancel</Button>
            <Button onClick={saveRole} data-testid="save-role"
              disabled={saving || (rForm.role === "store_admin" && rForm.location_ids.length === 0)}
              className="bg-[#4338CA] hover:bg-[#3730A3]">Update role</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add corporate admin</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="coadmin-name" />
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="coadmin-email" />
            </div>
            <div>
              <Label className="mb-1 block normal-case tracking-normal text-slate-600">Temporary password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="coadmin-password" />
              <p className="mt-1 text-xs text-slate-400">Minimum 8 characters — a fallback only. They'll get an email with a secure link to set their own password.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button onClick={createCoAdmin} data-testid="save-coadmin"
              disabled={saving || !form.name || !form.email || form.password.length < 8}
              className="bg-[#4338CA] hover:bg-[#3730A3]">Add admin</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
